/**
 * Guest Onboarding Store (Zustand + localStorage Persist)
 *
 * Persists onboarding task progress for unauthenticated (guest) users.
 * Tracks the 3 tasks achievable without authentication: quiz, search, chat.
 * On login, OnboardingChecklistContext merges this into the Firestore account
 * and clears the guest store.
 *
 * Follows the same pattern as pendingSaveStore.ts.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type GuestTaskId = 'quizCompleted' | 'firstSearch' | 'firstChat';

interface GuestOnboardingState {
  quizCompleted: boolean;
  firstSearch: boolean;
  firstChat: boolean;

  completeGuestTask: (taskId: GuestTaskId) => void;
  getGuestTasks: () => Record<GuestTaskId, boolean>;
  clearGuestTasks: () => void;
  hasAnyProgress: () => boolean;
}

export const useGuestOnboardingStore = create<GuestOnboardingState>()(
  persist(
    (set, get) => ({
      quizCompleted: false,
      firstSearch: false,
      firstChat: false,

      completeGuestTask: (taskId) => {
        set({ [taskId]: true });
      },

      getGuestTasks: () => ({
        quizCompleted: get().quizCompleted,
        firstSearch: get().firstSearch,
        firstChat: get().firstChat,
      }),

      clearGuestTasks: () => {
        set({ quizCompleted: false, firstSearch: false, firstChat: false });
      },

      hasAnyProgress: () => {
        const s = get();
        return s.quizCompleted || s.firstSearch || s.firstChat;
      },
    }),
    {
      name: 'indexalpha-guest-onboarding',
      partialize: (state) => ({
        quizCompleted: state.quizCompleted,
        firstSearch: state.firstSearch,
        firstChat: state.firstChat,
      }),
    }
  )
);
